import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { ExternalLink, Download, FileText, KeyRound, Loader2, Plus, MapPin } from "lucide-react";
import api from "@/lib/api";
import { COPPER } from "./VestaAuth";
import { getAs, useAsId, useVestaMe } from "./VestaViewAs";
import { openPdf } from "./VestaTools";
import { PraticaInfo } from "./VestaPraticaInfo";

const dt = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "—");
const TIPO = { fissaggi_inclinato: "Fissaggi tetto inclinato", fissaggi_piano: "Fissaggi tetto piano", zavorre: "Zavorre" };
const kwp = (v) => (v ? `${Number(v).toLocaleString("it-IT", { maximumFractionDigits: 2 })} kWp` : null);

export function useChiavi(kind) {
  const [d, setD] = useState(null);
  const asId = useAsId();
  useEffect(() => {
    setD(null);
    api.get("/vesta/chiavi/mappa", { params: { kind, ...(asId ? { as: asId } : {}) }, timeout: 90000 }).then((r) => setD(r.data)).catch(() => setD({ points: [], recent: [], count: {} }));
  }, [kind, asId]);
  return d;
}

const Fit = ({ pts }) => {
  const map = useMap();
  useEffect(() => {
    if (pts.length === 1) map.setView([pts[0].lat, pts[0].lng], 11);
    else if (pts.length > 1) map.fitBounds(pts.map((p) => [p.lat, p.lng]), { padding: [30, 30], maxZoom: 12 });
  }, [pts, map]);
  return null;
};

const Legend = ({ c, kind }) => (
  <div className="flex flex-wrap items-center gap-4 text-[11px] text-neutral-400" data-testid="chiavi-map-legend">
    <span className="flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-full ${kind === "tecnica" ? "bg-white" : ""}`} style={kind === "tecnica" ? {} : { background: COPPER }} />{kind === "tecnica" ? "Consulenze tecniche" : "Consulenze commerciali"} ({c.totale || 0})</span>
    {c.senza_posizione > 0 && <span className="text-neutral-600">{c.senza_posizione} senza indirizzo</span>}
  </div>
);

const PinPopup = ({ p, nav }) => (
  <div className="min-w-[180px] space-y-1 text-[12px] text-neutral-800" data-testid={`chiavi-pin-popup-${p.id}`}>
    <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: p.kind === "tecnica" ? "#555" : COPPER }}>{p.kind === "tecnica" ? TIPO[p.tipo] || "Consulenza tecnica" : p.tool_label || "Consulenza commerciale"}</div>
    <div className="flex items-center justify-between gap-2"><span className="text-sm font-bold">{p.cliente || "Cliente"}</span><span className="rounded-full bg-neutral-900"><PraticaInfo kind={p.kind === "tecnica" ? "ct" : "cfg"} id={p.id} testid={`chiavi-pin-info-${p.id}`} /></span></div>
    <div>{[p.comune, kwp(p.potenza_kwp), dt(p.date)].filter(Boolean).join(" · ")}</div>
    {p.kind === "tecnica" ? (p.has_pdf && <button type="button" onClick={() => openPdf(p.id)} className="mt-1 inline-flex items-center gap-1 font-bold" style={{ color: COPPER }} data-testid={`chiavi-pin-open-${p.id}`}><Download className="h-3.5 w-3.5" />Apri PDF</button>)
      : <button type="button" onClick={() => nav(`/vesta/configuratore?tool=${p.tool || ""}`)} className="mt-1 inline-flex items-center gap-1 font-bold" style={{ color: COPPER }} data-testid={`chiavi-pin-open-${p.id}`}><ExternalLink className="h-3.5 w-3.5" />Apri</button>}
  </div>
);

export function ChiaviMap({ d, kind }) {
  const nav = useNavigate();
  const pts = useMemo(() => {
    const seen = {};
    return (d?.points || []).map((p) => {
      const k = `${p.lat.toFixed(3)},${p.lng.toFixed(3)}`;
      const n = (seen[k] = (seen[k] ?? -1) + 1);
      return n ? { ...p, lat: p.lat + Math.sin(n * 2.1) * 0.004, lng: p.lng + Math.cos(n * 2.1) * 0.006 } : p;
    });
  }, [d]);
  return (
    <section className="mb-8 space-y-3" data-testid="chiavi-map">
      <style>{`.chiavi-dark-tiles{filter:invert(1) hue-rotate(180deg) brightness(.85) contrast(.9) saturate(.4)}`}</style>
      <div className="relative isolate h-[300px] overflow-hidden rounded-2xl border border-neutral-800 sm:h-[380px]">
        <MapContainer center={[45.55, 10.2]} zoom={7} scrollWheelZoom={false} className="h-full w-full" style={{ background: "#0b0b0b" }}>
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" className="chiavi-dark-tiles" />
          <Fit pts={pts} />
          {pts.map((p) => (
            <CircleMarker key={`${p.kind}-${p.id}`} center={[p.lat, p.lng]} radius={8}
              pathOptions={{ color: p.kind === "tecnica" ? "#fff" : COPPER, fillColor: p.kind === "tecnica" ? "#fff" : COPPER, fillOpacity: 0.85, weight: 2 }}>
              <Popup><PinPopup p={p} nav={nav} /></Popup>
            </CircleMarker>
          ))}
        </MapContainer>
        {!d && <div className="absolute inset-0 z-[500] flex items-center justify-center bg-black/60"><Loader2 className="h-6 w-6 animate-spin" style={{ color: COPPER }} /></div>}
        {d && !pts.length && <div className="pointer-events-none absolute inset-0 z-[500] flex items-center justify-center gap-2 text-sm text-neutral-400" data-testid="chiavi-map-empty"><MapPin className="h-4 w-4" />Nessuna consulenza con indirizzo</div>}
      </div>
      {d && <Legend c={d.count || {}} kind={kind} />}
    </section>
  );
}

export function ConsulenzaWide({ s, d }) {
  const nav = useNavigate();
  const me = useVestaMe();
  const asId = useAsId();
  const net = !!me?.network && !asId;
  const rec = d?.recent;
  return (
    <div className="col-span-full flex flex-col gap-5 rounded-2xl border border-neutral-800 bg-neutral-950 p-5 sm:p-7" data-testid="vesta-inner-cfg-consulenza">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <KeyRound className="h-8 w-8 shrink-0 sm:h-10 sm:w-10" style={{ color: COPPER }} strokeWidth={1.6} />
          <div>
            <div className="font-heading text-base font-extrabold leading-tight text-white sm:text-xl">{s.title}</div>
            <p className="mt-1 text-xs text-neutral-400">{s.desc}</p>
          </div>
        </div>
      </div>
      <div className="space-y-2" data-testid="chiavi-recent">
        <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: COPPER }}>Ultime consulenze</div>
        {!rec ? <Loader2 className="h-5 w-5 animate-spin" style={{ color: COPPER }} /> : !rec.length ? <p className="text-sm text-neutral-500" data-testid="chiavi-recent-empty">Nessuna consulenza ancora.</p>
          : rec.map((c) => (
            <div key={c.id} role="button" tabIndex={0} onClick={() => nav(`/vesta/configuratore?tool=${c.tool || ""}${c.id ? `&config_id=${encodeURIComponent(c.id)}` : ""}`)} data-testid={`chiavi-recent-${c.id}`}
              className="flex w-full cursor-pointer items-center gap-3 rounded-xl border border-neutral-800 px-3 py-2.5 text-left transition-colors hover:border-[#C17F59]">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-white">{c.cliente}</div>
                <div className="truncate text-xs text-neutral-400">{[net && c.partner, c.comune, kwp(c.potenza_kwp), dt(c.date)].filter(Boolean).join(" · ")}</div>
              </div>
              <PraticaInfo kind="cfg" id={c.id} testid={`chiavi-recent-info-${c.id}`} />
              <ExternalLink className="h-4 w-4 shrink-0 text-neutral-500" />
            </div>
          ))}
      </div>
      {!net && <button type="button" onClick={() => nav(s.to)} data-testid="chiavi-new-consulenza"
        className="flex items-center justify-center gap-2 rounded-full py-3 text-sm font-bold text-black transition-transform active:scale-[.98]" style={{ background: COPPER }}>
        <Plus className="h-4 w-4" /> Nuova consulenza
      </button>}
    </div>
  );
}

export function TecniciWide({ d }) {
  const rec = d?.recent;
  return (
    <div className="col-span-full mb-6 flex flex-col gap-4 rounded-2xl border border-neutral-800 bg-neutral-950 p-5 sm:p-7" data-testid="tec-recent-card">
      <div className="flex items-center gap-3">
        <FileText className="h-8 w-8 shrink-0 sm:h-10 sm:w-10" style={{ color: COPPER }} strokeWidth={1.6} />
        <div>
          <div className="font-heading text-base font-extrabold leading-tight text-white sm:text-xl">Ultimi calcoli tecnici</div>
          <p className="mt-1 text-xs text-neutral-400">Fissaggi e zavorre calcolati per i tuoi clienti.</p>
        </div>
      </div>
      <div className="space-y-2" data-testid="tec-recent">
        {!rec ? <Loader2 className="h-5 w-5 animate-spin" style={{ color: COPPER }} /> : !rec.length ? <p className="text-sm text-neutral-500" data-testid="tec-recent-empty">Nessun calcolo ancora.</p>
          : rec.map((c) => (
            <div key={c.id} className="flex items-center gap-3 rounded-xl border border-neutral-800 px-3 py-2.5" data-testid={`tec-recent-${c.id}`}>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-white">{c.cliente || "Cliente"}</div>
                <div className="truncate text-xs text-neutral-400">{[TIPO[c.tipo] || c.tipo, c.comune, kwp(c.potenza_kwp), dt(c.date)].filter(Boolean).join(" · ")}</div>
              </div>
              {c.has_pdf && <button type="button" onClick={() => openPdf(c.id)} data-testid={`tec-recent-pdf-${c.id}`}
                className="inline-flex shrink-0 items-center gap-1 rounded-full border border-neutral-700 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-neutral-200 transition-colors hover:border-[#C17F59] hover:text-[#C17F59]"><Download className="h-3.5 w-3.5" />PDF</button>}
            </div>
          ))}
      </div>
    </div>
  );
}
